import { useAuthStore } from '../../auth/store';
import { useShippingZones, useShippingMode } from '../hooks/useShippingZones';
import { formatPrice } from '../../../shared/utils/priceUtils';
import { ZoneType, RateType, ShippingMode, type ShippingZone } from '../api/shippingZonesApi';

const rateSummary = (zone: ShippingZone): string => {
  if (zone.rate.type === RateType.FREE) return 'Free';
  if (zone.rate.type === RateType.FLAT) {
    return zone.rate.flatAmountMinor != null ? `${formatPrice(zone.rate.flatAmountMinor)} flat` : 'Flat';
  }
  const count = zone.rate.weightTiers?.length ?? 0;
  return `${count} weight tier${count === 1 ? '' : 's'}`;
};

/**
 * TCS-admin READ-ONLY view of a merchant's shipping zones (PE-2172/PE-2173), mirroring
 * MerchantReturnPolicyPanel's read-only pattern on the merchant detail page.
 *
 * ⚠️ Unlike returns-policy, osprey-api's shipping-zones routes are scoped purely by the
 * `x-tenant-id` header ApiFetch derives from the globally-selected merchant — there is
 * no admin-by-id read. So this panel can only show live data when the merchant being
 * viewed IS the currently-selected merchant in the top switcher; otherwise it says so
 * rather than silently showing another merchant's (or no merchant's) zones.
 */
export function MerchantShippingZonesPanel({ merchantId }: { merchantId: string }) {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const isSelected = selectedTenantId === merchantId;

  const zonesQuery = useShippingZones();
  const modeQuery = useShippingMode();

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-foreground">Shipping zones</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configured by the merchant from their own dashboard — read-only here.
        </p>
      </div>

      {!isSelected ? (
        <p className="text-sm text-muted-foreground">
          Select this merchant in the switcher above to view their shipping zones.
        </p>
      ) : zonesQuery.isLoading || modeQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading shipping zones…</p>
      ) : zonesQuery.error ? (
        <p className="text-sm text-muted-foreground">Could not load this merchant's shipping zones.</p>
      ) : (
        <div>
          <div className="flex items-center justify-between py-2 border-b border-border">
            <span className="text-sm text-muted-foreground">Mode</span>
            <span className="text-sm font-medium text-foreground">
              {modeQuery.data?.mode === ShippingMode.ZONE_BASED ? 'Zone-based' : 'Courier'}
            </span>
          </div>
          {(zonesQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">No shipping zones configured.</p>
          ) : (
            (zonesQuery.data ?? []).map((zone) => (
              <div key={zone.id} className="flex items-start justify-between py-2 border-b border-border last:border-0">
                <span className="text-sm text-muted-foreground pr-4">
                  {zone.name}
                  {zone.type === ZoneType.LOCAL_PICKUP && ' (pickup)'}
                  {!zone.isActive && ' — inactive'}
                </span>
                <span className="text-sm font-medium text-foreground text-right">{rateSummary(zone)}</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
